<template>
  <div class="time-selection-wrapper">
    <v-card>
      <div aria-label="Calendar">
        <v-container>
          <div class="v-picker__title primary-color">
            <div class="v-date-picker-title">
              <div class="v-picker__title__btn v-date-picker-title__year v-picker__title__btn--active">{{selectedDateDetails.year}}
              </div>
              <div class="v-picker__title__btn v-date-picker-title_custom1 v-picker__title__btn--active">
                <div v-if="selectedDate">{{getDateWithFormat}}</div>
              </div>
            </div>
          </div>
          <div class="v-date-picker-header theme--light">
            <div>
                <v-btn class="v-btn v-btn--flat v-btn--icon v-btn--round theme--light v-size--default" ref='prevMonthID' :disabled='isDisabledPrevMonthBtn' :title="this.prevMonth" @click="changeMonth('prev')" :aria-controls="displayMonthYearID">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
            </div>
            <div>
              <div :id="displayMonthYearID" class="dateName" role="region" aria-live="polite" aria-atomic="true" aria-relevant="all">{{currDisplayMonthYear.month}} {{currDisplayMonthYear.year}}</div>
            </div>
            <div>
                <v-btn class="v-btn v-btn--flat v-btn--icon v-btn--round theme--light v-size--default" ref='nextMonthID' :disabled='isDisabledNextMonthBtn' :title="this.nextMonth" @click="changeMonth('next')" :aria-controls="displayMonthYearID">
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
            </div>
          </div>
          <div class="v-date-picker-table v-date-picker-table--date">
            <table role="presentation">
                <thead>
                <tr>
                    <th v-for="(day, index) in weekdays" :key="index">
                    <span :title="day.long">{{day.weekdayNarrow}}</span>
                    </th>
                </tr>
                </thead>
                <tbody @keydown="calendarNavigation($event, 'dateNav')">
                  <tr v-for="(week, i) in days.outputCalendar" :key="i">
                    <td v-for="(day, index) in week" :key="index">
                      <button :tabindex="day.allowDisplay ? 0 : -1"
                        @click="dateBtnSelected(day.dateLocal)"
                        :name='day.dateLocal'
                        :id='day.dateLocal'
                        class="v-btn v-btn--text v-btn--rounded theme--light"
                        :class="[day.allowDisplay ? '' : 'v-btn--disabled', day.dateLocal === selectedDate && day.allowDisplay ? 'active' : '']"
                        ref="calendar"
                        :aria-label=day.ariaString
                        >{{ day.date }}
                      </button>
                    </td>
                  </tr>
                </tbody>
            </table>
          </div>
        </v-container>
      </div>
    </v-card>
    <div class="timeslot-container">
      <div class="timeslots">
        <v-alert role="alert" v-if="availableTimeSlots.length === 0 && !loading">{{ messages.noTimeSlotsAvailable }}</v-alert>
        <v-btn-toggle
          class="timeslot-content"
          v-model="selectedTime"
          v-for="(timeslot, index) in availableTimeSlots"
          :key="index"
          ref="timeTable"
        >
          <v-btn
            :disabled="loading || (timeslot.time === selectedTime ? true :false )"
            text
            :ripple="false"
            class="timeslot"
            :value="timeslot.time"
            :id="'timeButton' + (index + 1)"
            @keydown="calendarNavigation($event, 'timeNav')"
          >{{timeslot.time | formatTime(selectedLanguage)}}</v-btn>
        </v-btn-toggle>
      </div>
    </div>
    <v-alert
      role="alert"
      :value="timeSlotNotReserved"
      type="error"
    >{{ messages.errorTimeslotNotReserved}}</v-alert>
    <v-alert
      role="alert"
      :value="timeSlotSessionValidation"
      type="error"
    >{{ messages.errorTimeSlotSessionValidation}}</v-alert>
    <v-alert
      role="alert"
      :value="timeSlotInvalidNumberOfPeople"
      type="error"
    >{{ messages.errorTimeSlotInvalidNumberOfPeople}}</v-alert>
    <v-alert
      role="alert"
      :value="timeSlotGenericServerError"
      type="error"
    >{{ messages.errorGenericReserve}}</v-alert>
    <v-progress-linear role="progressbar" v-if="loading" class="progress-bar" :indeterminate="loading"></v-progress-linear>
  </div>
</template>

<script>
import RestService from '../services/restService'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import moment from 'moment'

export default {
  name: 'TimeSelection',
  mixins: [RestService],
  props: [
    'reschedule',
    'rereserveTimeslotKey',
    'stepExpanded'
  ],
  data () {
    return {
      selectedDate: null,
      selectedTime: null,
      availableTimeSlots: [],
      availableDates: [],
      loading: false,
      timeSlotNotReserved: false,
      timeSlotSessionValidation: false,
      timeSlotInvalidNumberOfPeople: false,
      timeSlotGenericServerError: false,
      focusedDate: null,
      nextMonth: 'Next Month',
      prevMonth: 'Previous Month',
      displayMonthYearID: this._uid,
      currDisplayMonthYear: {
        date: new Date(),
        day: new Date().getDate(),
        monthNumber: new Date().getMonth(),
        month: '',
        year: new Date().getFullYear()
      },
      calendar: '',
      isDisabledPrevMonthBtn: false,
      isDisabledNextMonthBtn: false
    }
  },
  computed: {
    ...mapState([
      'configuration',
      'selectedServices',
      'selectedBranch',
      'messages',
      'selectedLanguage',
      'funcDebug',
      'reservedAppointmentId',
      'rescheduleSelectedDate'
    ]),
    ...mapGetters({
      customSlotLength: 'customSlotLength',
      isBranchAndServicesValid: 'isBranchAndServicesValid',
      getSecurityToken: 'getSecurityToken'
    }),
    locale () {
      return this.selectedLanguage.lang + '-' + this.selectedLanguage.countryCodeCountry
    },
    getDateWithFormat () {
      let momentDate = moment(this.selectedDate, 'YYYY-MM-DD', true)
      let format = !this.selectedLanguage.dateTimePickerFormat ? 'ddd , MMMM DD YYYY' : this.selectedLanguage.dateTimePickerFormat
      return momentDate.format(format)
    },
    firstDayOfWeek () {
      return this.selectedLanguage.firstDayOfWeek ? this.selectedLanguage.firstDayOfWeek : '1'
    },
    weekdays () {
      // Computed
      var weekdays = []
      var removeWeekdays = []
      var sortedWeekdays = []
      var outputWeekdays = []
      var current = new Date()
      for (var i = 0; i < 7; i++) {
        var dayNumber = current.getDay().toString()
        var weekdayShort = moment(current).format('ddd')
        var weekdayLong = moment(current).format('dddd')
        var weekdayNarrow = moment(current).format('ddd').substring(0, 2)
        weekdays.push({
          dayNumber,
          weekdayShort,
          weekdayLong,
          weekdayNarrow
        })
        current.setDate(current.getDate() + 1)
      }
      for (var j = 0; j < weekdays.length; j++) {
        if (weekdays[j].dayNumber === this.firstDayOfWeek) {
          for (var k = j; k < weekdays.length; k++) {
            sortedWeekdays.push(weekdays[k])
          }
          break
        } else {
          removeWeekdays.push(weekdays[j])
        }
      }
      outputWeekdays = sortedWeekdays.concat(removeWeekdays)
      return outputWeekdays
    },
    days () {
      var outputCalendar = []
      var processCalendar = []
      var enabledCurrentMonthDates = []
      var dayofWeek, date, weekday, month, year, dateLocal, allowDisplay, ariaString, monthNo
      var currentMonth = new Date(this.currDisplayMonthYear.year, this.currDisplayMonthYear.monthNumber, 1)
      var currentMonthFirstDay = new Date(this.currDisplayMonthYear.year, this.currDisplayMonthYear.monthNumber, 1).getDay() // firstday of current month
      var currentMonthLastDay = new Date(this.currDisplayMonthYear.year, this.currDisplayMonthYear.monthNumber + 1, 0) // lastday of current month
      var preFillDatesTotal = 0
      var postFillDatesTotal = 0

      // Past Month Dates to be filled in current calender view
      if (this.firstDayOfWeek > currentMonthFirstDay) {
        preFillDatesTotal = 7 - (this.firstDayOfWeek - currentMonthFirstDay)
      } else if (currentMonthFirstDay > this.firstDayOfWeek) {
        preFillDatesTotal = currentMonthFirstDay - this.firstDayOfWeek
      }
      var previousMonth = new Date(this.currDisplayMonthYear.year, this.currDisplayMonthYear.monthNumber, 0)
      var previousMonthLastDate = previousMonth.getDate()
      previousMonth.setDate(previousMonthLastDate - preFillDatesTotal)
      for (var i = 1; i <= preFillDatesTotal; i++) {
        previousMonth.setDate(previousMonth.getDate() + 1)
        dayofWeek = previousMonth.getDay()
        date = moment(previousMonth).format('D')
        if (date.length === 1) {
          date = '0' + date
        }
        weekday = moment(previousMonth).format('dddd')
        month = moment(previousMonth).format('MMMM')
        monthNo = previousMonth.getMonth() + 1
        if (monthNo.toString().length === 1) {
          monthNo = '0' + monthNo.toString()
        }
        year = moment(previousMonth).format('YYYY')
        ariaString = weekday + ', ' + month + ' ' + date + ', ' + year
        dateLocal = year + '-' + monthNo + '-' + date
        if (this.allowedDates(dateLocal) && date !== '') {
          allowDisplay = true
          enabledCurrentMonthDates.push(dateLocal)
        } else {
          allowDisplay = false
        }
        processCalendar.push({
          dayofWeek,
          date,
          weekday,
          month,
          year,
          ariaString,
          dateLocal,
          allowDisplay
        })
      }

      // Current Month Dates to be filled in current calender view
      for (var j = 1; j <= currentMonthLastDay.getDate(); j++) {
        currentMonth.setDate(j)
        dayofWeek = currentMonth.getDay()
        date = moment(currentMonth).format('D')
        if (date.length === 1) {
          date = '0' + date
        }
        weekday = moment(currentMonth).format('dddd')
        month = moment(currentMonth).format('MMMM')
        monthNo = currentMonth.getMonth() + 1
        if (monthNo.toString().length === 1) {
          monthNo = '0' + monthNo.toString()
        }
        year = moment(currentMonth).format('YYYY')
        ariaString = weekday + ', ' + month + ' ' + date + ', ' + year
        dateLocal = year + '-' + monthNo + '-' + date
        if (this.allowedDates(dateLocal) && date !== '') {
          allowDisplay = true
          enabledCurrentMonthDates.push(dateLocal)
        } else {
          allowDisplay = false
        }
        processCalendar.push({
          dayofWeek,
          date,
          weekday,
          month,
          year,
          ariaString,
          dateLocal,
          allowDisplay
        })
      }
      var noofWeeks = Math.ceil((currentMonthLastDay.getDate() + preFillDatesTotal) / 7) // calculate no of weeks in current month

      // Next Month Dates to be filled in current calender view
      postFillDatesTotal = (noofWeeks * 7) - (currentMonthLastDay.getDate() + preFillDatesTotal)
      var nextMonth = new Date(this.currDisplayMonthYear.year, this.currDisplayMonthYear.monthNumber + 1, 1)
      for (var q = 1; q <= postFillDatesTotal; q++) {
        nextMonth.setDate(q)
        dayofWeek = nextMonth.getDay()
        date = moment(nextMonth).format('D')
        if (date.length === 1) {
          date = '0' + date
        }
        weekday = moment(nextMonth).format('dddd')
        month = moment(nextMonth).format('MMMM')
        monthNo = nextMonth.getMonth() + 1
        if (monthNo.toString().length === 1) {
          monthNo = '0' + monthNo.toString()
        }
        year = moment(nextMonth).format('YYYY')
        ariaString = weekday + ', ' + month + ' ' + date + ', ' + year
        dateLocal = year + '-' + monthNo + '-' + date
        if (this.allowedDates(dateLocal) && date !== '') {
          allowDisplay = true
          enabledCurrentMonthDates.push(dateLocal)
        } else {
          allowDisplay = false
        }
        processCalendar.push({
          dayofWeek,
          date,
          weekday,
          month,
          year,
          ariaString,
          dateLocal,
          allowDisplay
        })
      }
      // calculate no rows to be displays in current calendar view based on no of weeks
      for (var week = 0; week < noofWeeks; week++) { outputCalendar[week] = [] }
      var weekCounter = 0
      var dayCounter = 0
      for (var d = 0; d < processCalendar.length; d++) {
        if (dayCounter < 7) dayCounter++
        else {
          weekCounter++
          dayCounter = 1
        }
        var pcal = processCalendar[d]
        outputCalendar[weekCounter].push(pcal)
      }
      monthNo = currentMonth.getMonth() + 1
      if (monthNo.toString().length === 1) {
        monthNo = '0' + monthNo.toString()
      }
      if (this.availableDates.length > 0) {
        var firstAvailable = this.availableDates[0].split('-')[1]
        var lastAvialable = this.availableDates[this.availableDates.length - 1].split('-')[1]
        if (firstAvailable === monthNo) {
          this.prevMonthIsDisabled(true)
          this.$nextTick(() => {
            // console.log(this)
            if (!this.days.enabledCurrentMonthDates.includes(this.selectedDate)) {
              this.$refs.nextMonthID.$el.focus()
            } else {
              for (var i = 0; i < this.$refs.calendar.length; i++) {
                if (this.$refs.calendar[i].name === this.selectedDate) {
                  this.$refs.calendar[i].focus()
                }
              }
            }
          })
        } else {
          this.prevMonthIsDisabled(false)
        }
        if (lastAvialable === monthNo) {
          this.nextMonthIsDisabled(true)
          this.$nextTick(() => {
            // console.log(this)
            if (!this.days.enabledCurrentMonthDates.includes(this.selectedDate)) {
              this.$refs.prevMonthID.$el.focus()
            } else {
              for (var i = 0; i < this.$refs.calendar.length; i++) {
                if (this.$refs.calendar[i].name === this.selectedDate) {
                  this.$refs.calendar[i].focus()
                }
              }
            }
          })
        } else {
          this.nextMonthIsDisabled(false)
        }
      }
      return {
        outputCalendar,
        processCalendar,
        enabledCurrentMonthDates,
        noofWeeks,
        preFillDatesTotal,
        postFillDatesTotal
      }
    },
    selectedDateDetails () {
      var day, month, year, weekday
      moment.locale(this.locale)
      if (this.selectedDate !== null) {
        var details = this.selectedDate.split('-')
        day = details[2]
        month = details[1]
        year = details[0]
        const date = new Date(year, month - 1, day)
        month = moment(date).format('MMMM')
        weekday = moment(date).format('ddd')
        // console.log(month)
      }
      return {
        day,
        month,
        weekday,
        year
      }
    }
  },
  watch: {
    locale () {
      if (this.funcDebug) console.log('TimeSelection:watch:locale')
      // update the month display when language change
      moment.locale(this.locale)
      this.changeMonth('')
    },
    selectedServices (newData, oldData) {
      if (this.funcDebug) console.log('TimeSelection:watch:selectedServices')
      // if both services and branches selection valid then load
      if (this.isBranchAndServicesValid) {
        this.reloadDatesAndTimes()
      }
    },
    selectedBranch (newData, oldData) {
      if (this.funcDebug) console.log('TimeSelection:watch:selectedBranch')
      // if both services and branches selection valid then load
      if (this.isBranchAndServicesValid) {
        this.reloadDatesAndTimes()
      }
    },
    selectedDate (newDate, oldDate) {
      if (this.funcDebug) console.log('TimeSelection:watch:selectedDate')
      // var _this = this
      this.loading = true
      if (this.reservedAppointmentId) {
        this.releaseTimeslot(this.reservedAppointmentId).then(response => {
          if (this.funcDebug) console.log('TimeSelection:watch:selectedDate:releaseTimeslot.then')
          this.getTimeSlots()
          this.$emit('timeslot-cleared')
        })
      } else if (newDate) {
        this.getTimeSlots()
      }
      this.selectedTime = null
      this.availableTimeSlots = []
      this.$emit('timeslot-cleared')
      this.setSelectedDate(this.selectedDate)
    },
    selectedTime (newTime, oldTime) {
      if (this.funcDebug) console.log('TimeSelection:watch:selectedTime')
      // var _this = this
      if (newTime && oldTime && !this.reschedule && this.reservedAppointmentId) {
        this.loading = true
        this.releaseTimeslot(this.reservedAppointmentId).then(response => {
          if (this.funcDebug) console.log('TimeSelection:watch:selectedTime:releaseTimeslot.then')
          this.reserveNewTimeslot()
        })
      } else if (newTime && !oldTime && !this.reschedule) {
        this.loading = true
        this.reserveNewTimeslot()
      } else if (this.reschedule) {
        this.$emit('time-selected', this.selectedTime, this.selectedDate)
      }
    },
    rereserveTimeslotKey () {
      if (this.funcDebug) console.log('TimeSelection:watch:rereserveTimeslotKey')
      if (this.reservedAppointmentId) {
        this.releaseTimeslot(this.reservedAppointmentId).then(response => {
          this.reserveNewTimeslot()
        })
      } else {
        this.reserveNewTimeslot()
      }
    }
  },
  methods: {
    ...mapMutations([
      'setTimeSelectedKeyDown'
    ]),
    ...mapActions([
      'setCondTotalCost',
      'setSelectedDate'
    ]),
    reloadDatesAndTimes () {
      if (this.funcDebug) console.log('TimeSelection:methods:reloadDatesAndTimes')
      this.loading = true
      if (this.reservedAppointmentId) {
        this.selectedTime = null
        this.releaseTimeslot(this.reservedAppointmentId).then(response => {
          if (this.funcDebug) console.log('TimeSelection:methods:reloadDatesAndTimes:releaseTimeslot.then')
          this.$emit('timeslot-cleared')
          this.reloadDatesAndTimesStep2()
        })
      }
      this.reloadDatesAndTimesStep2()
    },
    reloadDatesAndTimesStep2 () {
      if (this.funcDebug) console.log('TimeSelection:methods:reloadDatesAndTimesStep2')
      this.availableTimeSlots = []
      if (this.rescheduleSelectedDate !== null) {
        this.selectedDate = this.rescheduleSelectedDate // Set appointment current date as selected date
      } else {
        this.selectedDate = null // reset selected date to force return of new time slots
      }
      this.getAvailableDates(this.selectedBranch.id, this.selectedServices, this.customSlotLength).then(response => {
        if (this.funcDebug) console.log('TimeSelection:methods:reloadDatesAndTimesStep2:getAvailableDates.then')
        this.availableDates = []
        for (let i = 0; i < response.data.length; i++) {
          const allowedDay = response.data[i]
          this.availableDates.push(allowedDay.date)
        }
        const today = new Date().toISOString().slice(0, 10)
        if (this.availableDates.length > 0) {
          if (this.rescheduleSelectedDate === null) {
            if (this.availableDates.indexOf(today) !== -1) {
              this.selectedDate = today
            } else {
              this.selectedDate = this.availableDates[0]
            }
          } else {
            this.selectedDate = this.availableDates[0]
            if (this.availableDates[0].split('-')[1] !== this.selectedDate.split('-')[1]) {
              this.changeMonth('next')
            }
          }
          this.focusedDate = this.selectedDate
          this.dateBtnSelected(this.selectedDate)
        } else {
          // No dates have been found, so no times will be found, the loading has ended here
          // and we have to show the error message
          this.loading = false
        }
        // update the month display
        this.changeMonth('')
        this.setfocus()
      })
    },
    allowedDates (val) {
      return this.availableDates.indexOf(val) !== -1
    },
    getTimeSlots () {
      if (this.funcDebug) console.log('TimeSelection:methods:getTimeSlots')
      this.getAvailableTimes(this.selectedDate, this.selectedServices, this.customSlotLength, this.selectedBranch.id).then(response => {
        if (this.funcDebug) console.log('TimeSelection:methods:getTimeSlots:getAvailableTimes.then')
        this.availableTimeSlots = []
        for (let i = 0; i < response.data.length; i++) {
          let timeSlot = {}
          timeSlot.time = response.data[i].time
          this.availableTimeSlots.push(timeSlot)
        }
        this.loading = false
        if (this.$route.name === 'calendarbooking') {
          // this is the last unique action when both services and branches are preselected
          this.$emit('is-loaded')
        }
      })
    },
    reserveNewTimeslot () {
      if (this.funcDebug) console.log('TimeSelection:methods:reserveNewTimeslot')
      if (this.selectedTime) {
        this.reserveTimeSlot(this.selectedBranch, this.selectedDate, this.selectedTime, this.customSlotLength, this.selectedServices, this.getSecurityToken, this.configuration.addMultiServices).then(response => {
          if (this.funcDebug) console.log('TimeSelection:methods:reserveNewTimeslot:reserveTimeSlot.then')
          if (response.data.errorMessage !== 'ERROR_CANNOT_BE_RESERVED' && response.data.errorMessage !== 'ERROR_SESSION_VIOLATION' &&
                response.data.errorMessage !== 'ERROR_INVALID_NUMBER_OF_PEOPLE' && response.data.errorMessage !== 'ERROR_GENERIC') {
            const myReservedAppointmentId = response.data.publicId
            if (response.data.totalCost) {
              this.setCondTotalCost(response.data.totalCost)
            }
            this.timeSlotNotReserved = false
            this.timeSlotSessionValidation = false
            this.timeSlotInvalidNumberOfPeople = false
            this.timeSlotGenericServerError = false
            this.loading = false
            this.$emit(
              'time-selected',
              this.selectedTime,
              this.selectedDate,
              myReservedAppointmentId
            )
          } else {
            if (response.data.errorMessage === 'ERROR_CANNOT_BE_RESERVED') {
              this.timeSlotNotReserved = true
              this.timeSlotSessionValidation = false
              this.timeSlotInvalidNumberOfPeople = false
              this.timeSlotGenericServerError = false
            } else if (response.data.errorMessage === 'ERROR_INVALID_NUMBER_OF_PEOPLE') {
              this.timeSlotNotReserved = false
              this.timeSlotSessionValidation = false
              this.timeSlotInvalidNumberOfPeople = true
              this.timeSlotGenericServerError = false
            } else if (response.data.errorMessage === 'ERROR_SESSION_VIOLATION') {
              this.timeSlotNotReserved = false
              this.timeSlotSessionValidation = true
              this.timeSlotInvalidNumberOfPeople = false
              this.timeSlotGenericServerError = false
            } else {
              this.timeSlotNotReserved = false
              this.timeSlotSessionValidation = false
              this.timeSlotInvalidNumberOfPeople = false
              this.timeSlotGenericServerError = true
            }
            this.loading = false
            this.selectedTime = null
          }
        })
      }
    },
    changeMonth (event) {
      if (this.availableDates.length === 0) {
        this.prevMonthIsDisabled(true)
        this.nextMonthIsDisabled(true)
      }
      if (this.funcDebug) console.log('TimeSelection:methods:changeMonth')
      var cur = new Date(this.currDisplayMonthYear.date)
      if (event && (event.keyCode === 39 || event.keyCode === 40)) event = 'next'
      if (event && (event.keyCode === 37 || event.keyCode === 38)) event = 'prev'
      if (event === 'next') cur.setMonth(cur.getMonth() + 1)
      if (event === 'prev') cur.setMonth(cur.getMonth() - 1)
      this.currDisplayMonthYear = {}
      this.currDisplayMonthYear.date = new Date(cur)
      this.currDisplayMonthYear.year = new Date(cur).getFullYear()
      this.currDisplayMonthYear.monthNumber = new Date(cur).getMonth()
      this.currDisplayMonthYear.day = new Date(cur).getDate()
      this.currDisplayMonthYear.month = moment(new Date(cur)).format('MMMM')
    },
    calendarNavigation (event, navSection) {
      if (this.funcDebug) console.log('TimeSelection:methods:calendarNavigation')
      // Date selection keyboard Navigation
      if (navSection === 'dateNav') {
        for (var dIndex = 0; dIndex < this.$refs.calendar.length; dIndex++) {
          if (event.shiftKey && event.keyCode === 9) { // Shift Tab key Pressed
            event.preventDefault()
            if (!this.$refs.nextMonthID.$el.disabled) {
              this.$refs.nextMonthID.$el.focus()
            } else {
              this.$refs.prevMonthID.$el.focus()
            }
            break
          } else if (event.keyCode === 9) {
            event.preventDefault()
            this.$refs.timeTable[0].$children[0].$el.focus()
            break
          } else if (event.keyCode === 39 || event.keyCode === 40) { // right arrow and down arrow
            event.preventDefault()
            if (this.$refs.calendar[dIndex].name === this.focusedDate) {
              if (this.days.enabledCurrentMonthDates[this.days.enabledCurrentMonthDates.length - 1] !== this.$refs.calendar[dIndex].name) {
                var ii = dIndex
                while (this.days.enabledCurrentMonthDates.indexOf(this.$refs.calendar[ii + 1].name) === -1) {
                  ii++
                }
                this.$refs.calendar[ii + 1].focus()
                this.focusedDate = this.$refs.calendar[ii + 1].name
                break
              } else {
                if (this.availableDates[this.availableDates.length - 1] !== this.focusedDate) {
                  this.changeMonth(event)
                  this.$nextTick(function navNextTick () {
                    this.setFocusMonthChange(event)
                  })
                }
              }
            }
          } else if (event.keyCode === 37 || event.keyCode === 38) { // left arrow and up arrow
            event.preventDefault()
            if (this.$refs.calendar[dIndex].name === this.focusedDate) {
              if (this.days.enabledCurrentMonthDates[0] !== this.$refs.calendar[dIndex].name) {
                var jj = dIndex
                while (this.days.enabledCurrentMonthDates.indexOf(this.$refs.calendar[jj - 1].name) === -1) {
                  jj--
                }
                this.$refs.calendar[jj - 1].focus()
                this.focusedDate = this.$refs.calendar[jj - 1].name
                break
              } else {
                if (this.availableDates[0] !== this.focusedDate) {
                  this.changeMonth(event)
                  this.$nextTick(function navNextTick () {
                    this.setFocusMonthChange(event)
                  })
                }
              }
            }
          } else if (event.keyCode === 36) { // Home -  go to first cell of current week
            event.preventDefault()
            if (this.$refs.calendar[dIndex].name === this.focusedDate) {
              var homeRowData = this.$refs.calendar[dIndex].closest('tr').children
              for (var homeIndex = 0; homeIndex < homeRowData.length; homeIndex++) {
                if (this.days.enabledCurrentMonthDates.indexOf(homeRowData[homeIndex].querySelector('button').name) !== -1) {
                  homeRowData[homeIndex].querySelector('button').focus()
                  break
                }
              }
            }
          } else if (event.keyCode === 35) { // End -  go to last cell of current week
            event.preventDefault()
            if (this.$refs.calendar[dIndex].name === this.focusedDate) {
              var endRowData = this.$refs.calendar[dIndex].closest('tr').children
              for (var endIndex = endRowData.length - 1; endIndex >= 0; endIndex--) {
                if (this.days.enabledCurrentMonthDates.indexOf(endRowData[endIndex].querySelector('button').name) !== -1) {
                  endRowData[endIndex].querySelector('button').focus()
                  break
                }
              }
            }
          }
        }
      } else if (navSection === 'timeNav') { // Time Selection Keyboard Navigation
        if (event.keyCode === 13 || event.keyCode === 32) {
          this.setTimeSelectedKeyDown(true)
        }
        var curFocused = document.activeElement.id
        for (var tIndex = 0; tIndex < this.$refs.timeTable.length; tIndex++) {
          if ((event.shiftKey && event.keyCode === 9) && curFocused === 'timeButton1') { // shift Tab key Pressed
            event.preventDefault()
            if (this.days.enabledCurrentMonthDates.indexOf(this.selectedDate) !== -1) {
              var i = 0
              while (this.$refs.calendar[i].id !== this.selectedDate) {
                if (this.$refs.calendar[i + 1] === undefined) {
                  this.$refs.calendar[i].focus()
                } else {
                  i++
                }
              }
              this.$refs.calendar[i].focus()
              break
            } else {
              var pp = 0
              while (this.$refs.calendar[pp].id !== this.days.enabledCurrentMonthDates[this.days.enabledCurrentMonthDates.length - 1]) {
                pp++
              }
              this.$refs.calendar[pp].focus()
              break
            }
          } else {
            if (this.$refs.timeTable[tIndex].$children[0].$el.id === curFocused) {
              if ((event.keyCode === 37 || event.keyCode === 38) && this.$refs.timeTable[tIndex - 1] !== undefined) {
                if (this.$refs.timeTable[tIndex - 1].$children[0].$el.disabled === false) {
                  this.$refs.timeTable[tIndex - 1].$children[0].$el.focus()
                } else {
                  if (this.$refs.timeTable[tIndex - 2] !== undefined) {
                    this.$refs.timeTable[tIndex - 2].$children[0].$el.focus()
                  }
                }
                break
              } else if ((event.keyCode === 39 || event.keyCode === 40) && this.$refs.timeTable[tIndex + 1] !== undefined) {
                if (this.$refs.timeTable[tIndex + 1].$children[0].$el.disabled === false) {
                  this.$refs.timeTable[tIndex + 1].$children[0].$el.focus()
                } else {
                  if (this.$refs.timeTable[tIndex + 2] !== undefined) {
                    this.$refs.timeTable[tIndex + 2].$children[0].$el.focus()
                  }
                }
                break
              }
            }
          }
        }
      }
    },
    setFocusMonthChange (event) { // change month to prev or next ans set the focus to first enabled date
      if (this.funcDebug) console.log('TimeSelection:methods:setFocusMonthChange')
      for (var i = 0; i < this.$refs.calendar.length; i++) {
        if (event.keyCode === 39 || event.keyCode === 40) {
          if (this.$refs.calendar[i].name === this.days.enabledCurrentMonthDates[0]) {
            this.$refs.calendar[i].focus()
            this.focusedDate = this.$refs.calendar[i].name
            break
          }
        } else if (event.keyCode === 37 || event.keyCode === 38) {
          var length = this.days.enabledCurrentMonthDates.length - 1
          if (this.$refs.calendar[i].name === this.days.enabledCurrentMonthDates[length] && this.days.enabledCurrentMonthDates[length] !== undefined) {
            this.$refs.calendar[i].focus()
            this.focusedDate = this.$refs.calendar[i].name
            break
          }
        }
      }
    },
    dateBtnSelected (date) {
      this.selectedDate = date
    },
    focusChanged (event) {
      if (this.funcDebug) console.log('TimeSelection:methods:focusChanged')
      this.focusedDate = event.target.name
    },
    keydownFocusChanged (event) {
      if (this.funcDebug) console.log('TimeSelection:methods:keydownFocusChanged')
      // We don't want to focus on the time if the step is not expanded
      if (!this.stepExpanded) {
        return
      }
      var focusedElement = ''
      if (document.activeElement.id === 'dateTimePanel') {
        focusedElement = document.activeElement.id
      }
      if (focusedElement === 'dateTimePanel' && event.keyCode === 9) {
        if (!event.shiftKey) {
          event.preventDefault()
          if (this.days.enabledCurrentMonthDates.indexOf(this.selectedDate) !== -1) {
            this.focusedDate = this.selectedDate
          } else {
            this.focusedDate = this.days.enabledCurrentMonthDates[0]
          }
          for (var i = 0; i < this.$refs.calendar.length; i++) {
            if (this.$refs.calendar[i].name === this.focusedDate) {
              this.$refs.calendar[i].focus()
            }
          }
        }
      }
    },
    setfocus () {
      if (this.funcDebug) console.log('TimeSelection:methods:setfocus')
      this.$nextTick(() => {
        for (var dIndex = 0; dIndex < this.$refs.calendar.length; dIndex++) {
          if (this.$refs.calendar[dIndex].name === this.selectedDate) {
            this.$refs.calendar[dIndex].focus()
          }
        }
      })
    },
    prevMonthIsDisabled (value) {
      this.isDisabledPrevMonthBtn = value
    },
    nextMonthIsDisabled (value) {
      this.isDisabledNextMonthBtn = value
    }
  },
  mounted () {
    if (this.funcDebug) console.log('TimeSelection:mounted')
    // set the locale on moment
    moment.locale(this.locale)
    // load Dates only if we branch and services valid
    if (this.isBranchAndServicesValid) {
      this.loading = true
      this.getAvailableDates(this.selectedBranch.id, this.selectedServices, this.customSlotLength).then(response => {
        if (this.funcDebug) console.log('TimeSelection:mounted:getAvailableDates.then')
        this.availableDates = []
        for (let i = 0; i < response.data.length; i++) {
          const allowedDay = response.data[i]
          this.availableDates.push(allowedDay.date)
        }
        var today = new Date()
        var date = today.getDate()
        if (date.toString().length === 1) {
          date = '0' + date.toString()
        }
        var month = today.getMonth() + 1
        if (month.toString().length === 1) {
          month = '0' + month.toString()
        }
        today = today.getFullYear() + '-' + month + '-' + date
        if (this.availableDates.indexOf(today) !== -1) {
          if (this.selectedDate !== null) {
            this.focusedDate = this.selectedDate
          } else {
            this.focusedDate = today
            this.selectedDate = this.focusedDate
          }
        } else {
          if (this.availableDates.length > 0) {
            if (this.availableDates[0].split('-')[1] !== month.toString()) {
              this.changeMonth('next')
            }
            this.focusedDate = this.availableDates[0]
            this.selectedDate = this.focusedDate
          }
        }
        // update the month display
        this.changeMonth('')
        this.loading = false
      })
    }
    document.addEventListener('focusin', this.focusChanged)
    document.addEventListener('keydown', this.keydownFocusChanged)
  }
}
</script>

<style>
.time-selection-wrapper .date-picker {
  margin: 0;
  border-bottom: 1px solid var(--input-border-color) !important;
  box-shadow: none;
}

.v-date-picker-table {
  padding: 16px;
  height: auto;
}

.v-picker__title {
  border-radius: 0;
}

.date-picker .v-date-picker-title .v-picker__title__btn {
  font-size: 18px;
  font-weight: normal;
  text-align: center;
  color: var(--primary-btn-text-color) !important;
  display: block;
  margin: 0;
  padding: 0;
  line-height: 24px;
}

.v-picker .v-date-picker-years li.active{
  color: #fff !important;
}

.dateName {
  font-weight: bold;
}

.timeslot {
  background-color: var(--primary-btn-bg-color) !important;
  color: var(--primary-btn-text-color) !important;
  font-weight: 500;
  font-size: 16px;
  width:88px;
}

.v-date-picker-table .v-btn {
  font-size: 16px;
}

.v-date-picker-table--date.time-selection-step .v-btn:hover,
.v-date-picker-table.v-date-picker-table--month.time-selection-step .v-btn:hover {
  height: 40px;
  width: 40px;
  color: var(--default-font-color) !important;
}

.picker-reverse-transition-leave,
.picker-reverse-transition-leave-active,
.picker-reverse-transition-leave-to,
.picker-transition-leave,
.picker-transition-leave-active,
.picker-transition-leave-to {
  right: 0;
  left: 0;
}

.active {
  color: var(--primary-btn-text-color) !important;
  background-color: var(--focused-element) !important;
}

.v-date-picker-header .v-btn.v-btn--icon .v-icon {
  font-size: 36px;
}

.v-date-picker-table th {
  color: var(--default-font-color) !important;
  font-size: 16px;
  border-bottom: 1px solid #e5e9f0;
}

.timeslots {
  justify-content: center;
  padding: 16px;
  background-color: var(--timeslots-container-bg-color) !important;
}

.timeslots .timeslot:focus {
  width: auto !important;
  border: none !important;
}

.timeslot-content {
  margin: 5px;
}

.timeslot-content.v-btn-toggle.v-btn-toggle--selected {
  box-shadow: none;
}

.timeslot-content.v-btn-toggle .v-btn {
  opacity: 1;
  height: auto;
  border-radius: 0 !important;
  font-size: 16px;
  padding: 8px;
}
.timeslot-content.v-btn-toggle .v-btn:focus {
  border: 3px solid var(--focused-element) !important;
  background-color: var(--wrapper-bg-color-primary) !important;
  color: var(---default-font-color) !important;
  width: 88px !important;
}

.timeslot-content .timeslot.v-btn.v-btn--active {
  background-color: var(--focused-element) !important;
  color: var(--primary-btn-text-color) !important;
}

@media screen and (max-width: 768px) {
  .timeslot-container {
    text-align: center;
  }
}

@media screen and (width: 768px) {
  .timeslot-container {
    text-align: left;
  }
  .timeslot {
    width: 120px;
  }
  .timeslot-content.v-btn-toggle .v-btn:focus {
    width: 120px !important;
  }
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .time-selection-wrapper .v-date-picker-table--date .v-btn.v-btn--active,
  .time-selection-wrapper
    .v-date-picker-table.v-date-picker-table--month
    .v-btn.v-btn--active {
    color: var(--primary-btn-text-color) !important;
    background-color: var(--focused-element) !important;
  }
  .timeslot-content.v-btn-toggle .v-btn:focus {
    color: black !important;
  }
}

.v-date-picker-title_custom1 {
  font-size: 22px !important;
  text-align: left !important;
  font-weight: 500 !important;
  position: relative !important;
  overflow: hidden !important;
  padding-bottom: 8px !important;
  margin-bottom: -8px !important;
}
</style>
